/**
 * PATH: packages/email-hub/src/feature/components/filter-sidebar/SavedViewsSection.tsx
 * FILE_ID: EMAILHUB-FILTERSIDEBAR-SAVEDVIEWS-001
 * PURPOSE: Renders saved email-filter presets and their row actions.
 */
import { Filter, Pin, Trash2 } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import {
  useEmailFilterPresets,
  type FilterPreset,
} from '../../hooks/useEmails'
import { SectionHeader } from './SidebarChrome'
import type { SectionProps } from './types'

interface SavedViewsSectionProps extends SectionProps {
  presetsApi: ReturnType<typeof useEmailFilterPresets>
  onApplyPreset: (filters: Record<string, unknown>) => void
}

export function SavedViewsSection({
  expanded,
  onToggle,
  presetsApi,
  onApplyPreset,
}: SavedViewsSectionProps) {
  const { presets, remove, update } = presetsApi
  return (
    <div className="mb-3">
      <SectionHeader
        label="Saved views"
        expanded={expanded}
        onToggle={onToggle}
        count={presets.length}
      />
      {expanded && (
        <div className="space-y-0.5">
          {presets.length === 0 ? (
            <p className="text-[10px] text-muted-foreground px-2 italic">
              Save a filter set to recall it later.
            </p>
          ) : (
            presets.map((preset) => (
              <SavedViewRow
                key={preset.id}
                preset={preset}
                onApply={() => onApplyPreset(preset.filters)}
                onTogglePin={async () => {
                  await update(preset.id, { is_pinned: !preset.is_pinned })
                }}
                onDelete={async () => {
                  if (window.confirm(`Delete saved view "${preset.name}"?`)) {
                    await remove(preset.id)
                  }
                }}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

function SavedViewRow({
  preset,
  onApply,
  onTogglePin,
  onDelete,
}: {
  preset: FilterPreset
  onApply: () => void
  onTogglePin: () => void
  onDelete: () => void
}) {
  return (
    <div className="group flex items-center gap-1 px-1 py-0.5 rounded hover:bg-accent">
      <button
        onClick={onApply}
        className="flex-1 text-left text-xs text-foreground/80 truncate flex items-center gap-1.5"
      >
        {preset.is_pinned ? (
          <Pin className="h-2.5 w-2.5 text-primary shrink-0" />
        ) : (
          <Filter className="h-2.5 w-2.5 text-muted-foreground/50 shrink-0" />
        )}
        <span className="truncate">{preset.name}</span>
      </button>
      <button
        onClick={onTogglePin}
        title={preset.is_pinned ? 'Unpin' : 'Pin'}
        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-muted rounded"
      >
        <Pin
          className={cn(
            'h-2.5 w-2.5',
            preset.is_pinned ? 'text-primary' : 'text-muted-foreground',
          )}
        />
      </button>
      <button
        onClick={onDelete}
        title="Delete"
        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-destructive/10 text-destructive rounded"
      >
        <Trash2 className="h-2.5 w-2.5" />
      </button>
    </div>
  )
}
